// Cookie management utility for CRM
// Consistent with main LiveLoveSpain site implementation

class CookieManager {
  constructor() {
    this.defaultOptions = {
      expires: 365, // days
      path: '/',
      sameSite: 'Lax'
    };
  }

  /**
   * Set a cookie
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {object} options - Cookie options (expires in days, path, etc.)
   */
  set(name, value, options = {}) {
    const opts = { ...this.defaultOptions, ...options };
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (opts.expires) {
      const date = new Date();
      date.setTime(date.getTime() + opts.expires * 24 * 60 * 60 * 1000);
      cookieString += `; expires=${date.toUTCString()}`;
    }

    if (opts.path) {
      cookieString += `; path=${opts.path}`;
    }

    if (opts.domain) {
      cookieString += `; domain=${opts.domain}`;
    }

    if (opts.sameSite) {
      cookieString += `; SameSite=${opts.sameSite}`;
    }

    if (opts.secure) {
      cookieString += `; Secure`;
    }

    document.cookie = cookieString;
  }

  /**
   * Get a cookie value
   * @param {string} name - Cookie name
   * @returns {string|null} - Cookie value or null if not found
   */
  get(name) {
    const nameEQ = encodeURIComponent(name) + '=';
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1, cookie.length);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
      }
    }

    return null;
  }

  /**
   * Remove a cookie
   * @param {string} name - Cookie name
   * @param {object} options - Cookie options (path, domain, etc.)
   */
  remove(name, options = {}) {
    const opts = { ...this.defaultOptions, ...options };
    this.set(name, '', { ...opts, expires: -1 });
  }

  /**
   * Check if user has given consent
   * @param {string} consentType - Type of consent (e.g., 'cookieConsent', 'adsConsent')
   * @returns {boolean} - True if consent was given
   */
  hasConsent(consentType) {
    return this.get(consentType) === 'true';
  }

  /**
   * Check if user has withdrawn consent
   * @param {string} consentType - Type of consent
   * @returns {boolean} - True if consent was explicitly withdrawn
   */
  hasWithdrawnConsent(consentType) {
    return this.get(consentType) === 'false';
  }

  /**
   * Check if user has made a consent decision
   * @param {string} consentType - Type of consent
   * @returns {boolean} - True if user has made a decision (accepted or declined)
   */
  hasMadeConsentDecision(consentType) {
    const value = this.get(consentType);
    return value === 'true' || value === 'false';
  }
}

// Export singleton instance
export const cookieManager = new CookieManager();


